import { useWorkbookOperations } from '@/hooks/content-generator/use-workbook-operations';
import { useAddGoalMutation } from '@/hooks/goal-planner/mutations/use-add-goal-mutation';
import { useGoalWorkbookJunction } from '@/hooks/goal-planner/use-goal-workbook-junction';
import { useProjectGeneration } from '@/hooks/content-generator/use-project-generation';
import { supabase } from '@/integrations/supabase/client';
import { createRecommendationTrackingWrapper } from './createRecommendationTrackingWrapper';
import { MessageDisplay } from '../../types/chat-hook-types';
import { useCallback, useState } from 'react';

/**
 * Simplified project creation mutation that follows the standard mutation pattern
 * This directly implements the project creation logic without complex state management
 */
const useSimpleProjectCreationMutation = () => {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  const { createWorkbook } = useWorkbookOperations();
  const { mutateAsync: createGoal } = useAddGoalMutation();
  const { createJunction } = useGoalWorkbookJunction();
  const { generateProjectData } = useProjectGeneration();
  
  const mutateAsync = useCallback(async (params: { userGoal: string; title: string; description: string; type: string }) => {
    setIsPending(true);
    setError(null);
    
    try {
      console.log('[SimpleProjectCreation] Starting project creation:', params);
      
      // Step 1: Generate Project Plan
      const projectData = await generateProjectData({ userGoal: params.userGoal });
      console.log('[SimpleProjectCreation] Generated project data:', projectData);
      
      // Step 2: Create Workbook (AI Agent)
      const workbook = await createWorkbook({
        title: params.userGoal, // FIXED: Use user's actual project name
        metadata: {
          originalUserInput: params.userGoal,
          aiGenerated: {
            title: projectData.title, // Store AI suggestion as metadata
            goal: projectData.goal,
            description: projectData.description,
            imagePrompt: projectData.imagePrompt,
            followUpQuestions: projectData.followUpQuestions,
            generatedAt: new Date().toISOString()
          }
        }
      });
      console.log('[SimpleProjectCreation] Created workbook:', workbook);
      
      // 🎯 COVER IMAGE GENERATION: Fire-and-forget during agent project creation
      if (projectData.imagePrompt) {
        console.log('[SimpleProjectCreation] Triggering cover image generation during agent creation');
        try {
          // Import the utility function dynamically to avoid circular dependencies
          const { generateCoverImageAndSave } = await import('@/utils/auto-cover-image-generation');
          
          // Fire-and-forget: Generate cover image in background without blocking project creation
          generateCoverImageAndSave(workbook.id, projectData.imagePrompt)
            .then((imageUrl) => {
              if (imageUrl) {
                console.log('[SimpleProjectCreation] Cover image generated successfully during agent creation:', imageUrl);
              }
            })
            .catch((error) => {
              console.warn('[SimpleProjectCreation] Cover image generation failed (non-critical):', error);
            });
        } catch (error) {
          // Silent failure - never block project creation for cover image issues
          console.warn('[SimpleProjectCreation] Cover image generation setup failed (non-critical):', error);
        }
      }
      
      // Step 3: Create Goal
      const goal = await createGoal({
        title: projectData.goal,
        description: projectData.description
      });
      console.log('[SimpleProjectCreation] Created goal:', goal);
      
      // Step 4: Create Junction
      await createJunction({
        goalId: goal.id,
        workbookId: workbook.id,
        agentThreadId: goal.agentThreadId!
      });
      console.log('[SimpleProjectCreation] Created junction');
      
      // Step 5: Update agent thread title
      if (goal.agentThreadId) {
        try {
          await supabase
            .from('agent_threads')
            .update({ 
              title: `Chat: ${projectData.title}`,
              updated_at: new Date().toISOString()
            })
            .eq('id', goal.agentThreadId);
          console.log('[SimpleProjectCreation] Updated agent thread title');
        } catch (titleUpdateError) {
          console.warn('[SimpleProjectCreation] Failed to update agent thread title:', titleUpdateError);
          // Non-critical error, continue
        }
      }
      
      const result = {
        id: workbook.id,
        generatedProjectId: workbook.id,
        generatedGoalId: goal.id
      };
      
      console.log('[SimpleProjectCreation] Project creation successful:', result);
      setIsPending(false);
      return result;
      
    } catch (err: any) {
      console.error('[SimpleProjectCreation] Project creation failed:', err);
      const error = new Error(err.message || 'Failed to create project');
      setError(error);
      setIsPending(false);
      throw error;
    }
  }, [generateProjectData, createWorkbook, createGoal, createJunction]);
  
  return {
    mutateAsync,
    isPending,
    isLoading: isPending,
    error
  };
};

/**
 * Project creation with recommendation tracking
 * Auto-generated using the standardized factory pattern
 * Reuses existing project creation infrastructure with mutation adapter
 */
export const useAgentChatProjectCreationWithTracking = (message: MessageDisplay | string) => {
  // Apply tracking wrapper to the simplified mutation
  const trackingWrapper = createRecommendationTrackingWrapper(
    useSimpleProjectCreationMutation,
    'project',
    {
      // Extract meaningful metadata for project tracking
      getMetadata: (params, result) => ({
        projectTitle: params.title || 'Untitled Project',
        projectType: params.type || 'Other',
        goalId: result.generatedGoalId,
        projectId: result.generatedProjectId,
        userGoal: params.userGoal
      }),
      // Use the project ID from the result
      getSavedItemId: (result) => result.generatedProjectId
    }
  );
  
  return trackingWrapper(message);
}; 